// ✅ UNIFICADO: Normalizador único para regras vindas de /cdr/rules/:pro (clássico)
// e /cdr/rules/producao/:pro/:orc/:oit (produção)
// Usado pelo RulesService para manter um contrato único de regras.

export interface SmbCreds {
	username?: string;
	password?: string;
	password_enc?: string;
	domain?: string | null;
}

export interface DirectorySecurity {
	smb?: SmbCreds;
}

export interface CanonicalRules {
	regra_id?: number;
	regra_ativo?: boolean;
	diretorio_nome?: string;
	diretorio_base_interno?: string;
	diretorio_base_externo?: string;
	diretorio_base_exportpng?: string;
	diretorio_base_export_prod?: string;
	diretorios?: {
		producao?: string;
		layout_png?: string;
		nome?: string;
	};
	dimensoes?: {
		largura_cm?: number;
		altura_cm?: number;
		horario_corte?: string;
	};
	seguranca?: {
		smb?: SmbCreds;
	};
	security?: {
		interno?: DirectorySecurity;
		externo?: DirectorySecurity;
		exportpng?: DirectorySecurity;
		diretorio_base_interno?: DirectorySecurity;
	};
	horario_corte?: string;
	largura_maxima_cm?: number;
	altura_maxima_cm?: number;
	padroes_cdr?: {
		versao_arquivo?: string;
		perfil_icc?: string;
		compactar?: boolean;
		salvar_com_visualizacao?: boolean;
	};
	produto?: any; // Preservar dados do produto se existirem
	hash?: any; // Preservar hash se existir (produção)
	use_pipeline?: boolean;
	use_pipeline_salvamento?: boolean;
	valida_dimensao?: boolean;
	valida_metragem?: boolean;
}

function coerceString(v: any): string | undefined {
	return typeof v === "string" && v.trim().length > 0 ? v : undefined;
}

function coerceNumber(v: any): number | undefined {
	const n = Number(v);
	return Number.isFinite(n) ? n : undefined;
}

function pickSmb(src: any): SmbCreds | undefined {
	if (!src || typeof src !== "object") return undefined;
	const username = coerceString(src.username);
	const password = coerceString(src.password);
	const password_enc = coerceString(src.password_enc);
	const domain =
		typeof src.domain === "string" || src.domain === null
			? src.domain
			: undefined;
	if (!username && !password && !password_enc && typeof domain === "undefined")
		return undefined;
	return { username, password, password_enc, domain };
}

/**
 * ✅ NORMALIZAÇÃO UNIFICADA de regras de produção e vendas
 * @param input - Dados brutos da API
 * @param opts - Opções de normalização (variant: 'pro' | 'producao')
 * @returns Objeto normalizado com campos consistentes
 */
export function normalizeRules(
	input: any,
	opts?: { variant?: "pro" | "producao" },
): CanonicalRules {
	const variant: "pro" | "producao" | undefined = opts?.variant;
	const out: CanonicalRules = {};

	if (!input || typeof input !== "object") return out;

	// ✅ NOVO: Preservar IDs de regra e produto (produção)
	if (input.regra?.id) out.regra_id = coerceNumber(input.regra.id);
	if (input.regra?.ativo !== undefined)
		out.regra_ativo = Boolean(input.regra.ativo);
	if (input.produto) out.produto = input.produto;
	if (input.hash) out.hash = input.hash;

	// Variante PRODUCAO: campos mais novos
	if (variant === "producao" || (input.diretorios && input.dimensoes)) {
		out.diretorios = {
			producao: coerceString(input?.diretorios?.producao),
			layout_png: coerceString(input?.diretorios?.layout_png),
			nome: coerceString(input?.diretorios?.nome),
		};
		out.diretorio_nome =
			coerceString(input?.diretorios?.nome) ||
			coerceString(input?.diretorio_nome);

		// ✅ NOVO: Mapear para campos legacy (compatibilidade)
		out.diretorio_base_export_prod = out.diretorios?.producao;
		out.diretorio_base_exportpng = out.diretorios?.layout_png;

		out.dimensoes = {
			largura_cm: coerceNumber(input?.dimensoes?.largura_cm),
			altura_cm: coerceNumber(input?.dimensoes?.altura_cm),
			horario_corte: coerceString(input?.dimensoes?.horario_corte),
		};
		out.horario_corte =
			out.dimensoes.horario_corte || coerceString(input?.horario_corte);
		out.largura_maxima_cm =
			out.dimensoes.largura_cm ?? coerceNumber(input?.largura_maxima_cm);
		out.altura_maxima_cm =
			out.dimensoes.altura_cm ?? coerceNumber(input?.altura_maxima_cm);
	}

	// Variante PRO (clássico)
	if (
		variant === "pro" ||
		(!out.diretorio_nome && (input?.diretorio_base_interno || input?.seguranca))
	) {
		out.diretorio_base_interno =
			coerceString(input?.diretorio_base_interno) || out.diretorios?.producao;
		out.diretorio_base_externo =
			coerceString(input?.diretorio_base_externo) || out.diretorios?.layout_png;
		out.diretorio_nome =
			coerceString(input?.diretorio_nome) ||
			out.diretorio_nome ||
			out.diretorios?.nome;
	}

	// Fallbacks gerais (cobrem o payload clássico /cdr/rules/:pro_id)
	if (!out.horario_corte) {
		out.horario_corte = coerceString(input?.horario_corte);
	}
	if (typeof out.largura_maxima_cm === "undefined") {
		out.largura_maxima_cm = coerceNumber(input?.largura_maxima_cm);
	}
	if (typeof out.altura_maxima_cm === "undefined") {
		out.altura_maxima_cm = coerceNumber(input?.altura_maxima_cm);
	}

	// Segurança/SMB
	const smb = pickSmb(input?.seguranca?.smb) || pickSmb(input?.security?.smb);
	if (smb) {
		out.seguranca = { smb };
		out.security = out.security || {};
		out.security.diretorio_base_interno = { smb };
		// Mantém compatibilidade com consumidores que esperam .interno
		out.security.interno = out.security.interno || { smb };
		out.security.externo = out.security.externo || { smb }; // ✅ NOVO: exportação externa
	}

	// Padrões CDR
	if (input?.padroes_cdr && typeof input.padroes_cdr === "object") {
		out.padroes_cdr = {
			versao_arquivo: coerceString(input.padroes_cdr.versao_arquivo),
			perfil_icc: coerceString(input.padroes_cdr.perfil_icc),
			compactar:
				typeof input.padroes_cdr.compactar === "boolean"
					? input.padroes_cdr.compactar
					: undefined,
			salvar_com_visualizacao:
				typeof input.padroes_cdr.salvar_com_visualizacao === "boolean"
					? input.padroes_cdr.salvar_com_visualizacao
					: undefined,
		};
	}

	if (typeof input.valida_dimensao === "boolean") out.valida_dimensao = input.valida_dimensao;
	if (typeof input.valida_metragem === "boolean") out.valida_metragem = input.valida_metragem;
	if (typeof input.use_pipeline === "boolean") out.use_pipeline = input.use_pipeline;
	if (typeof input.use_pipeline_salvamento === "boolean") out.use_pipeline_salvamento = input.use_pipeline_salvamento;

	return out;
}

export function maskRulesForLog(r: CanonicalRules): any {
	try {
		const json = JSON.stringify(r, (k, v) => {
			const key = String(k).toLowerCase();
			if (key.includes("password")) return "***";
			return v;
		});
		return JSON.parse(json);
	} catch {
		return r;
	}
}
